import React from 'react';
import { Card, Steps } from 'antd-mobile';
import { useStore } from '../store/useStore';

const { Step } = Steps;

interface HealthMilestone {
    title: string;
    description: string;
    durationMinutes: number; // Time to reach this milestone in minutes
}

const milestones: HealthMilestone[] = [
    { title: '20 นาที', description: 'เลิกระหว่างรอรถเมล์ ชีพจรกลับมาเต้นปกติ ความดันเลือดลดลง', durationMinutes: 20 },
    { title: '8 ชั่วโมง', description: 'เลิกตอนนอน ออกซิเจนในเลือดเพิ่มขึ้น คาร์บอนมอนอกไซด์ลดลงครึ่งนึง', durationMinutes: 8 * 60 },
    { title: '24 ชั่วโมง', description: 'เลิก 1 วัน ลดความเสี่ยงหัวใจวาย ปอดเริ่มขับเสมหะ', durationMinutes: 24 * 60 },
    { title: '48 ชั่วโมง', description: 'เลิก 2 วัน ปลายประสาทเริ่มฟื้นตัว การรู้รสและกลิ่นดีขึ้น', durationMinutes: 48 * 60 },
    { title: '72 ชั่วโมง', description: 'เลิก 3 วัน หลอดลมขยายตัว หายใจโล่งขึ้น พลังงานเพิ่มขึ้น', durationMinutes: 72 * 60 },
    { title: '2 สัปดาห์', description: 'เลิกครึ่งเดือน การไหลเวียนเลือดดีขึ้น เดินเหินได้คล่อง ไม่หอบง่าย', durationMinutes: 14 * 24 * 60 },
];

export const HealthTimeline: React.FC = () => {
    const { user } = useStore();

    if (!user || (!user.quitDate && user.mode !== 'reduce')) {
        // Show timeline for everyone but calculate progress based on quitDate only? 
        // Or if 'reduce', show generic info. 
        // Let's hide if no quit date is set for now to simplify.
        return null;
    }

    // Calculate minutes since quit
    let minutesSinceQuit = 0;
    if (user.quitDate) {
        const now = new Date();
        const quitDate = new Date(user.quitDate);
        const diffMs = now.getTime() - quitDate.getTime();
        minutesSinceQuit = Math.max(0, diffMs / (1000 * 60));
    }

    // Determine current milestone
    let currentStepIndex = -1;
    for (let i = 0; i < milestones.length; i++) {
        if (minutesSinceQuit >= milestones[i].durationMinutes) {
            currentStepIndex = i;
        } else {
            break;
        }
    }

    // Ant Design Mobile Steps: 'current' index is the one "in progress" or "just finished"? 
    // It usually highlights up to 'current'. 
    // If currentStepIndex is 0 (20 mins achieved), we want to show 0 as finished.
    // Let's pass currentStepIndex + 1 to 'current' prop to indicate completed steps?
    // Actually, Steps 'current' usually 0-indexed. 
    // If current is 1, steps 0 is finished, 1 is in progress.
    // If we want to show completed, we can use status 'finish' for previous ones.

    // Logic:
    // If minutesSinceQuit < 20 mins, currentStepIndex = -1. Step 0 is 'wait'.
    // If minutesSinceQuit > 20 mins, currentStepIndex = 0. Step 0 is 'finish'. Step 1 is 'process'.

    return (
        <div style={{ marginTop: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>
                เส้นทางสุขภาพดี 🫁
            </h3>
            <Card style={{ borderRadius: 20 }}>
                <Steps direction="vertical" current={currentStepIndex + 1}>
                    {milestones.map((m, index) => {
                        const isUnlocked = index <= currentStepIndex;
                        const isNext = index === currentStepIndex + 1;

                        return (
                            <Step
                                key={index}
                                title={
                                    <span style={{
                                        color: isUnlocked ? '#10b981' : isNext ? '#0f172a' : '#94a3b8',
                                        fontWeight: isUnlocked || isNext ? 700 : 500
                                    }}>
                                        {m.title} {isUnlocked && '✅'}
                                    </span>
                                }
                                description={
                                    <span style={{ fontSize: 13, color: isUnlocked || isNext ? '#64748b' : '#cbd5e1' }}>
                                        {m.description}
                                    </span>
                                }
                                status={isUnlocked ? 'finish' : isNext ? 'process' : 'wait'}
                            />
                        );
                    })}
                </Steps>
            </Card>
        </div>
    );
};
